import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import * as Models from '../_shared/models/models-index';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap, take } from 'rxjs/operators';
import { Enums, IEnums } from '../_shared/enums/enums';
import { IAuthenticationInfo, IRouteDetails } from '../_shared/models/models-index';
import { ConfigurationService, RuntimeConfigService } from '../_shared/services/services-index';
import { environment } from '../../environments/environment';

@Injectable()
export class LayoutHelperService {

  constructor(
    private router: Router,
    private configService: ConfigurationService,
    private breakpointObserver: BreakpointObserver,
  ) { }

  private static readonly MOBILE_BREAKPOINT = 992;

  filterModel: Models.IFilterModel;
  private sideNavToggleSubject = new BehaviorSubject<'open' | 'closed'>(!!environment.sidebarDefaultOpen ? 'open' : 'closed');
  sideNavToggleAction$ = this.sideNavToggleSubject.asObservable();
  routingConfig: Models.IRoutingConfig;
  enums: IEnums = Enums;
  currentCategory: string;
  expandedCategory: string;
  menuClass: string;
  currentChildren: IRouteDetails[];
  rotateMenuGlyph = false;
  rotateCategoryGlyph = false;

  isMobileView$ = this.breakpointObserver
    .observe([`(max-width: ${LayoutHelperService.MOBILE_BREAKPOINT - 1}px)`])
    .pipe(map(result => result.matches));

  toggleSidebar(toggleStatus: 'open' | 'closed') {
    const nextState = toggleStatus === 'open' ? 'closed' : 'open';
    this.setSidebarState(nextState);
  }

  setSidebarState(state: 'open' | 'closed') {
    this.rotateMenuGlyph = state === 'open';
    this.sideNavToggleSubject.next(state);
  }

  ensureMobileSidebarExpanded(): void {
    if (this.isMobileView()) {
      this.setSidebarState('open');
    }
  }

  updateExpandedCategory(category: string) {
    if (this.expandedCategory === category) {
      this.expandedCategory = this.currentCategory;
    } else {
      this.expandedCategory = category;
    }
  }

  updateCurrentCategory(): void {
    this.currentCategory = this.router.url.split('/')[1] || 'leads';
    this.expandedCategory = this.currentCategory;
  }

  initializeSidebarToggleState(): void {
    const defaultToggleState = !!environment.sidebarDefaultOpen ? 'open' : 'closed';
    this.setSidebarState(defaultToggleState);
    this.ensureMobileSidebarExpanded();
  }

  private isMobileView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < LayoutHelperService.MOBILE_BREAKPOINT;
  }

  userHasLevelAccess(userRole: string, requiredRole: string): boolean {
    if (this.configService.role.isSysAdminRole(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole);
    }
    else if (this.configService.role.isCorporateRole(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
    }
    else if (this.configService.role.isOrg5Role(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
        || this.configService.role.isOrg5Role(userRole)
        ? true : false;
    }
    else if (this.configService.role.isOrg4Role(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
        || this.configService.role.isOrg5Role(userRole)
        || this.configService.role.isOrg4Role(userRole)
        ? true : false;
    }
    else if (this.configService.role.isOrg3Role(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
        || this.configService.role.isOrg5Role(userRole)
        || this.configService.role.isOrg4Role(userRole)
        || this.configService.role.isOrg3Role(userRole)
        ? true : false;
    }
    else if (this.configService.role.isOrg2Role(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
        || this.configService.role.isOrg5Role(userRole)
        || this.configService.role.isOrg4Role(userRole)
        || this.configService.role.isOrg3Role(userRole)
        || this.configService.role.isOrg2Role(userRole)
        ? true : false;
    }
    else if (this.configService.role.isOrg1Role(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
        || this.configService.role.isOrg5Role(userRole)
        || this.configService.role.isOrg4Role(userRole)
        || this.configService.role.isOrg3Role(userRole)
        || this.configService.role.isOrg2Role(userRole)
        || this.configService.role.isOrg1Role(userRole)
        ? true : false;
    }
    else if (this.configService.role.isDealerRole(requiredRole)) {
      return this.configService.role.isSysAdminRole(userRole)
        || this.configService.role.isCorporateRole(userRole)
        || this.configService.role.isOrg5Role(userRole)
        || this.configService.role.isOrg4Role(userRole)
        || this.configService.role.isOrg3Role(userRole)
        || this.configService.role.isOrg2Role(userRole)
        || this.configService.role.isOrg1Role(userRole)
        || this.configService.role.isDealerRole(userRole)
        ? true : false;
    } else {
      return false;
    }
  }
}
